/**
 * @fileoverview Shared error parsing and display utilities for error boundaries.
 */

import { isRouteErrorResponse } from 'react-router';
import type { SxProps, Theme } from '@mui/material';

/**
 * Parsed error information for display in error boundaries.
 */
export interface ParsedError {
  message: string;
  details?: string;
}

/**
 * Parses a route error into a user-friendly message and optional details.
 * Handles React Router error responses and standard Error objects.
 */
export function parseRouteError(error: unknown, defaultMessage: string): ParsedError {
  let message = defaultMessage;
  let details: string | undefined;

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      message = 'Page not found';
    } else {
      message = error.statusText || defaultMessage;
    }
    details = error.data?.message;
  } else if (error instanceof Error) {
    message = error.message;
    details = process.env.NODE_ENV === 'development' ? error.stack : undefined;
  }

  return { message, details };
}

/**
 * Shared sx props for error details pre-formatted box.
 */
export const errorDetailsSx: SxProps<Theme> = {
  mt: 2,
  p: 2,
  bgcolor: 'background.paper',
  borderRadius: 1,
  overflow: 'auto',
  fontSize: '0.75rem',
  maxHeight: 250,
};
